import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { ZoomableImage } from "@/components/ZoomableImage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, BookOpen, CheckCircle2, Circle, Loader2, FileText,
  ChevronLeft, ChevronRight, Award, Lock, Crown, ClipboardCheck, Quote, Clock, Headphones, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import Exam from "@/pages/Exam";
import ProvaPeace from "@/pages/ProvaPeace";
import LectioTest from "@/pages/LectioTest";
import { LIVELLO, examLabel } from "@/lib/labels";
import { LessonInfographic } from "@/components/LessonInfographic";

const readingMinutes = (text) => {
  if (!text) return 1;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export default function Course() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasAccess = user?.has_access;
  const [course, setCourse] = useState(null);
  const [view, setView] = useState("overview"); // overview | lesson | exam
  const [lessonIndex, setLessonIndex] = useState(0);
  const [lesson, setLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);

  const load = useCallback(
    () => api.get(`/courses/${courseId}`).then((res) => setCourse(res.data)),
    [courseId],
  );
  useEffect(() => { load(); }, [load]);

  const openLesson = async (idx) => {
    if (idx > 0 && !hasAccess) {
      navigate("/abbonamento");
      return;
    }
    if (course?.mathema && course.lectio_unlocked && !course.lectio_unlocked[idx]) {
      toast.info("Modalità Máthema: supera la Prova di comprensione della lectio precedente per sbloccare questa.");
      return;
    }
    setView("lesson");
    setLessonIndex(idx);
    setLesson(null);
    setLessonLoading(true);
    try {
      const { data } = await api.get(`/courses/${courseId}/lesson/${idx}`);
      setLesson(data);
    } catch (e) {
      if (e.response?.status === 402) navigate("/abbonamento");
      else if (e.response?.status === 423) { toast.info(e.response.data.detail); setView("overview"); }
      else toast.error("Errore nel caricamento della lectio");
    } finally {
      setLessonLoading(false);
    }
  };

  const onTestPassed = async () => {
    await load();
    setLesson((l) => (l ? { ...l, completata: true } : l));
  };

  if (!course)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
        </div>
      </div>
    );

  const completed = course.progress.completed_lessons || [];
  const pct = Math.round((completed.length / course.argomenti.length) * 100);
  const exam = course.progress.exam;

  if (view === "exam")
    return course.trasversale
      ? <ProvaPeace courseId={courseId} onExit={() => { setView("overview"); load(); }} />
      : <Exam courseId={courseId} corso={course.nome} capolavoro={courseId === "erm"} onExit={() => { setView("overview"); load(); }} />;

  if (view === "lectiotest")
    return (
      <LectioTest
        courseId={courseId}
        index={lessonIndex}
        argomento={course.argomenti[lessonIndex]}
        onPassed={onTestPassed}
        onExit={() => openLesson(lessonIndex)}
      />
    );

  return (
    <div className="min-h-screen">
      <Navbar />

      {view === "overview" && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          <button onClick={() => navigate("/")} data-testid="back-to-dashboard"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Bottega
          </button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="rounded-full border-border">{LIVELLO[course.anno]}</Badge>
                <Badge variant="outline" className="rounded-full border-border">{course.cfu} ⭐ stelle</Badge>
                {exam?.superato && (
                  <Badge className={`rounded-full ${(exam.a_regola_arte ?? exam.lode) ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}>
                    {exam.esito || exam.grado}
                  </Badge>
                )}
              </div>
              <h1 className="font-serif text-4xl font-bold mb-2">{course.nome}</h1>
              <p className="text-sm text-primary mb-1">Maestro: {course.docente}</p>
              <p className="text-xs text-muted-foreground mb-4">In questo livello studi con {course.docente}.</p>
              <p className="text-muted-foreground leading-relaxed mb-8 max-w-2xl">{course.descrizione}</p>

              {course.trasversale && (
                <div className="flex items-start gap-2 bg-primary/[0.04] border border-primary/25 rounded-md p-3 mb-8 max-w-2xl text-xs text-muted-foreground" data-testid="officina-disclaimer">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-primary" strokeWidth={1.5} />
                  <span>Percorso di divulgazione filosofica. La filosofia offre strumenti di riflessione: non sostituisce consulenza medica o psicologica.</span>
                </div>
              )}

              <h2 className="font-serif text-xl font-bold mb-4">Programma del corso</h2>
              <div className="space-y-2">
                {course.argomenti.map((a, i) => {
                  const done = completed.includes(i);
                  const mathemaLocked = course.mathema && course.lectio_unlocked && !course.lectio_unlocked[i];
                  const locked = (i > 0 && !hasAccess) || mathemaLocked;
                  return (
                    <button
                      key={a}
                      onClick={() => openLesson(i)}
                      data-testid={`lesson-item-${i}`}
                      className="w-full text-left flex items-center gap-3 p-4 bg-card border border-border rounded-md transition-all hover:border-primary/40 hover:-translate-y-0.5 group"
                    >
                      {locked
                        ? <Lock className="h-5 w-5 text-muted-foreground/50 shrink-0" strokeWidth={1.5} />
                        : done
                          ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                          : <Circle className="h-5 w-5 text-muted-foreground/40 shrink-0" strokeWidth={1.5} />}
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          Lectio {i + 1}{i === 0 && !hasAccess ? " · Gratis" : ""}
                        </p>
                        <p className="font-medium group-hover:text-primary transition-colors">{a}</p>
                      </div>
                      {mathemaLocked
                        ? <Badge variant="outline" className="rounded-full border-accent/40 text-accent-foreground/80 text-xs whitespace-nowrap">Supera la Prova precedente</Badge>
                        : locked
                          ? <Badge variant="outline" className="rounded-full border-border text-muted-foreground text-xs">Bloccata</Badge>
                          : <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: progress + exam */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                <div className="bg-card border border-border rounded-md p-5">
                  <h3 className="font-serif text-lg font-bold mb-4">Avanzamento</h3>
                  <div className="flex items-center gap-3 mb-3">
                    <Progress value={pct} className="h-2" />
                    <span className="text-sm font-semibold tabular-nums">{pct}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {completed.length} di {course.argomenti.length} lectiones comprese
                  </p>
                </div>

                <div className="bg-card border border-border rounded-md p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    <h3 className="font-serif text-lg font-bold">{examLabel(courseId)}</h3>
                  </div>
                  {exam?.superato ? (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Award className={`h-6 w-6 ${(exam.a_regola_arte ?? exam.lode) ? "text-accent" : "text-primary"}`} strokeWidth={1.5} />
                        <span className="font-serif text-2xl font-bold">{exam.esito || exam.grado}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{exam.giudizio}</p>
                      <Button variant="outline" className="w-full rounded-sm" data-testid="retake-exam-btn"
                        onClick={() => setView("exam")}>
                        Ripeti la Prova
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">
                        {courseId === "erm"
                          ? "L'opera con cui l'apprendista dimostra di essere diventato maestro."
                          : "Mettiti alla prova davanti al Maestro. Il risultato ti assegna un grado di maestria."}
                      </p>
                      {hasAccess ? (
                        <Button className="w-full rounded-sm bg-primary hover:bg-primary/90" data-testid="start-exam-btn"
                          onClick={() => setView("exam")}>
                          {courseId === "erm" ? "Affronta il Capolavoro" : "Affronta la Prova del Maestro"}
                        </Button>
                      ) : (
                        <Button className="w-full rounded-sm bg-primary hover:bg-primary/90" data-testid="unlock-exam-btn"
                          onClick={() => navigate("/abbonamento")}>
                          <Lock className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {courseId === "erm" ? "Sblocca il Capolavoro" : "Sblocca la Prova"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {view === "lesson" && (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
          <button onClick={() => { setView("overview"); load(); }} data-testid="back-to-course"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {course.nome}
          </button>

          {lessonLoading || !lesson ? (
            <div className="py-24 flex flex-col items-center gap-4 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
              <p className="text-muted-foreground">Il Maestro sta preparando la lectio e il disegno...</p>
              <p className="text-xs text-muted-foreground">La prima volta può richiedere fino a un minuto.</p>
            </div>
          ) : (
            <article className="grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2">
                <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-2">
                  Lectio {lessonIndex + 1}
                </p>
                <h1 className="font-serif text-4xl font-bold mb-4 leading-tight">{lesson.argomento}</h1>
                <div className="flex items-center gap-3 mb-8 flex-wrap" data-testid="lesson-meta">
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="lesson-reading-time">
                    <Clock className="h-4 w-4" strokeWidth={1.5} /> ~{readingMinutes(lesson.content)} min di lettura
                  </span>
                  <button
                    type="button"
                    disabled
                    title="Ascolta la lezione come podcast — presto disponibile"
                    data-testid="lesson-audio-btn"
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-border text-muted-foreground cursor-not-allowed opacity-70"
                  >
                    <Headphones className="h-4 w-4" strokeWidth={1.5} /> Ascolta
                    <span className="text-xs">(in arrivo)</span>
                  </button>
                </div>
                <div
                  className="prose-philo select-none"
                  data-testid="lesson-content"
                  onContextMenu={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}
                >
                  <ReactMarkdown components={{
                    img: ({ src, alt }) => <ZoomableImage src={src} alt={alt} />,
                    a: ({ href, children }) => (href && href.startsWith("/")
                      ? <a href={href} onClick={(e) => { e.preventDefault(); navigate(href); }}
                          className="text-primary underline underline-offset-2 hover:text-accent cursor-pointer"
                          data-testid="approfondimento-link">{children}</a>
                      : <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">{children}</a>)
                  }}>{lesson.content}</ReactMarkdown>
                </div>

                <LessonInfographic lesson={lesson} index={lessonIndex} />

                <div className="mt-12 pt-6 border-t border-border">
                {lesson.completata ? (
                  <div className="flex items-center gap-2 mb-6" data-testid="lectio-passed-badge">
                    <Badge className="bg-primary text-primary-foreground rounded-full px-3 py-1.5">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Lectio compresa
                    </Badge>
                    <span className="text-sm text-muted-foreground">Prova di comprensione superata.</span>
                  </div>
                ) : (
                  <div className="bg-secondary/50 border border-border rounded-md p-5 mb-6">
                    <div className="flex items-center gap-2 mb-1">
                      <ClipboardCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
                      <h3 className="font-serif text-lg font-bold">Prova di comprensione</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Completa le 12 domande per superare questa lectio. Servono almeno 9 risposte corrette.
                      Se non la superi dovrai ripeterla (massimo 2 tentativi al giorno).
                    </p>
                    <Button className="rounded-sm bg-primary hover:bg-primary/90"
                      onClick={() => setView("lectiotest")} data-testid="start-lectiotest-btn">
                      <ClipboardCheck className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Inizia la Prova di comprensione
                    </Button>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Button variant="ghost" disabled={lessonIndex === 0}
                    onClick={() => openLesson(lessonIndex - 1)} data-testid="prev-lesson-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Precedente
                  </Button>
                  <Button variant="ghost" disabled={lessonIndex >= course.argomenti.length - 1}
                    onClick={() => openLesson(lessonIndex + 1)} data-testid="next-lesson-btn">
                    Successiva <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="lg:sticky lg:top-24 space-y-5">
                  {lesson.image && (
                    <figure className="rounded-md border border-border overflow-hidden bg-[#f4f1ea]" data-testid="lesson-sketch">
                      <img src={lesson.image} alt={`Disegno a tema: ${lesson.argomento}`}
                        className="w-full object-cover" style={{ filter: "grayscale(1)" }} />
                      <figcaption className="text-[11px] text-muted-foreground px-3 py-2 text-center italic">
                        Illustrazione a tema · carboncino
                      </figcaption>
                    </figure>
                  )}

                  {lesson.password && (
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="lesson-password">
                      <p className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-1">Password</p>
                      <p className="font-serif text-2xl font-bold text-primary mb-1">{lesson.password}</p>
                      {lesson.password_significato && (
                        <p className="text-xs text-muted-foreground leading-relaxed">{lesson.password_significato}</p>
                      )}
                    </div>
                  )}

                  {lesson.citazione && (
                    <blockquote className="rounded-md border border-border bg-card p-4" data-testid="lesson-citazione">
                      <Quote className="h-4 w-4 text-accent mb-2" strokeWidth={1.5} />
                      <p className="font-serif text-base italic leading-relaxed mb-2">"{lesson.citazione}"</p>
                      <footer className="text-xs text-muted-foreground">— {lesson.citazione_autore}</footer>
                    </blockquote>
                  )}
                </div>
              </aside>
            </article>
          )}
        </div>
      )}
    </div>
  );
}
